import { AppConstants } from '@/helpers/app.constants';
import { TrackingHelper } from '@/helpers/tracking.helper';
import { Utils } from '@/helpers/utils';
import { AlertService } from '@/services/alert.service';
import { formatDate, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { DocumentSummary, PropertyMaintenance, RealProperty, TrackingService } from '@eemo/api-client';

import { ObjectUpdatedType } from '@/enums/ObjectUpdatedType.enum';
import { AppConfigService } from '@/services/app-config.service';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Subscription } from 'rxjs';
import { InputDecimalValueDirective } from '../../extensions/inputDecimalValueDirective';
import { InputNullValueDirective } from '../../extensions/inputNullValueDirective';
import { InputTrimDirective } from '../../extensions/inputTrimDirective';
import { ModalBaseComponent } from '../base/modal-base/modal-base.component';
import { DocAddWidgetComponent } from '../doc-add-widget/doc-add-widget.component';

export class MaintenanceDoneDialogData {
  prop: RealProperty;
  maintenance: PropertyMaintenance;
}

@Component({
  selector: 'app-maintenance-done-dialog',
  templateUrl: './maintenance-done-dialog.component.html',
  styleUrls: ['./maintenance-done-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    NgIf,
    NgTemplateOutlet,
    MatInputModule,
    MatDatepickerModule,
    InputTrimDirective,
    InputNullValueDirective,
    InputDecimalValueDirective,
    MatButtonModule,
    DocAddWidgetComponent,
    ModalBaseComponent
  ]
})
export class MaintenanceDoneDialogComponent implements OnInit, OnDestroy {
  isBrowserSupportsDateInput = Utils.isBrowserSupportsDateInput;

  subs: Subscription[] = [];
  doneForm: UntypedFormGroup;
  dialogTitle: string;
  formSubmitting = false;

  document: DocumentSummary;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MaintenanceDoneDialogData,
    private trackingService: TrackingService,
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    public trackingHelper: TrackingHelper,
    public dialogRef: MatDialogRef<MaintenanceDoneDialogComponent>,
    private appConfigService: AppConfigService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.dialogTitle = `Effectuer l'entretien "${data.maintenance.summary}"`;
  }

  // convenience getter for easy access to form fields
  f(field: string): AbstractControl {
    return this.doneForm.get(field);
  }

  createDateNotFutureValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }
      if (new Date(value) > new Date()) return { dateFuture: true };
      return null;
    };
  }

  ngOnInit() {
    this.doneForm = this.fb.group({
      date: [null, [Validators.required, this.createDateNotFutureValidator()]],
      comments: [null, Validators.maxLength(160)],
      cost: [null, [Validators.pattern(AppConstants.FORM_PATTERN_POSITIVE_DECIMAL), Validators.min(0)]],
      company: [null, Validators.maxLength(50)]
    });
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }

  onDocSelected(doc: DocumentSummary) {
    if (doc) {
      this.document = doc;
    }
  }

  onDocDeleted(doc: DocumentSummary) {
    if (doc) {
      // doc deleted
      this.document = null;
    }
  }
  onSubmit() {
    if (this.doneForm.valid) {
      this.formSubmitting = true;
      // parse the date to format date send by mat datepicker (browser with support of input type="date" doesn't need it)
      const date = new Date(this.f('date').value);
      this.f('date').setValue(formatDate(date, 'yyyy-MM-dd', this.locale));

      this.subs.push(
        this.trackingService
          .doRealPropertyMaintenance(this.data.prop.uid, this.data.maintenance.uid, this.doneForm.value)
          .subscribe({
            next: (createdId) => {
              if (this.document) {
                this.addMaintenanceDocument();
              }
              this.dialogRef.close(createdId);
              this.alertService.success('Ajout effectuée');
              this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.MAINTENANCE);
            },
            error: () => {
              this.dialogRef.close();
            }
          })
      );
    }
  }

  addMaintenanceDocument() {
    this.data.maintenance.documents.push(this.document);
    this.subs.push(
      this.trackingService
        .updateRealPropertyMaintenance(this.data.prop.uid, this.data.maintenance.uid, this.data.maintenance)
        .subscribe(() => this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.MAINTENANCE))
    );
  }
  onNoClick(): void {
    this.dialogRef.close(false);
  }
}
